import { useState, useCallback } from "react";
import { Download, Share } from "lucide-react";
import { Button } from "./button";
import { ExportFormatDialog } from "./export-format-dialog";
import useExportDialog from "../../hooks/useExportDialog";

/**
 * Unified export component for all report pages.
 *
 * Usage patterns:
 *
 * 1) Standalone (button + dialog):
 *    <ReportExportButton title="Export Daily Report" getExcelData={...} getPdfContent={...} />
 *
 * 2) Controlled dialog (no button, opened externally):
 *    <ReportExportButton open={open} onOpenChange={setOpen} showButton={false} ... />
 *
 * 3) PDF-only (button triggers direct download, no dialog):
 *    <ReportExportButton getPdfContent={...} />
 */
const ReportExportButton = ({
  title = "Export Report",
  getExcelData,
  getPdfContent,
  buttonLabel = "Export",
  variant = "outline",
  size,
  className,
  "data-testid": testId = "export-btn",
  // Controlled dialog mode
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  showButton = true,
  // Phone: icon-only square (share arrow); full label from sm up
  compact = false,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const { exportToExcel, exportToPdf } = useExportDialog();

  const isControlled = controlledOpen !== undefined;
  const dialogOpen = isControlled ? controlledOpen : internalOpen;
  const setDialogOpen = isControlled ? controlledOnOpenChange : setInternalOpen;

  const handleExport = useCallback(
    (format) => {
      if (format === "excel" && getExcelData) {
        const data = getExcelData();
        if (data) exportToExcel(data.filename, data.csvContent);
      } else if (format === "pdf" && getPdfContent) {
        const data = getPdfContent();
        if (data) exportToPdf(data.title, data.htmlContent, data.customStyles);
      }
    },
    [getExcelData, getPdfContent, exportToExcel, exportToPdf]
  );

  const handleClick = useCallback(() => {
    if (!getExcelData && getPdfContent) {
      handleExport("pdf");
    } else {
      setDialogOpen(true);
    }
  }, [getExcelData, getPdfContent, handleExport, setDialogOpen]);

  return (
    <>
      {showButton && compact ? (
        <Button
          variant={variant}
          onClick={handleClick}
          className={`h-9 w-9 p-0 sm:h-10 sm:w-auto sm:px-4 ${className || ""}`}
          aria-label={buttonLabel}
          data-testid={testId}
        >
          <Share className="w-4 h-4 sm:hidden" />
          <Download className="w-4 h-4 mr-2 hidden sm:inline" />
          <span className="hidden sm:inline">{buttonLabel}</span>
        </Button>
      ) : showButton && (
        <Button
          variant={variant}
          size={size}
          onClick={handleClick}
          className={className}
          data-testid={testId}
        >
          <Download className="w-4 h-4 mr-2" />
          {buttonLabel}
        </Button>
      )}
      {getExcelData && (
        <ExportFormatDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          onExport={handleExport}
          title={title}
        />
      )}
    </>
  );
};

export { ReportExportButton };
